import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';

interface FeedbackItem {
  id: string;
  type: string;
  message: string;
  email: string | null;
  userId: string | null;
  metadata: any;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const TYPE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  bug: { label: 'Bug Report', icon: '🐛', color: 'bg-red-100 text-red-700 border-red-200' },
  feature: { label: 'Feature Request', icon: '💡', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  question: { label: 'Question', icon: '❓', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  other: { label: 'Other', icon: '💬', color: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'reviewed', label: 'Reviewed', color: 'bg-blue-100 text-blue-800' },
  { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-800' },
];

export function AdminFeedback() {
  const { getAccessTokenSilently } = useAuth0();
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadFeedback();
  }, [filterStatus, filterType]);

  async function loadFeedback() {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const filters: any = {};
      if (filterStatus) filters.status = filterStatus;
      if (filterType) filters.type = filterType;
      const res = await api.getFeedbackList(token, filters);
      setFeedback(res.feedback || []);
    } catch (err) {
      console.error('Failed to load feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(feedbackId: string, newStatus: string) {
    try {
      const token = await getAccessTokenSilently();
      await api.updateFeedbackStatus(feedbackId, newStatus, token);
      setFeedback(prev =>
        prev.map(f => f.id === feedbackId ? { ...f, status: newStatus } : f)
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update status');
    }
  }

  const statusCounts = {
    all: feedback.length,
    new: feedback.filter(f => f.status === 'new').length,
    reviewed: feedback.filter(f => f.status === 'reviewed').length,
    resolved: feedback.filter(f => f.status === 'resolved').length,
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Feedback & Issues</h1>
            <p className="text-sm text-gray-600 mt-1">View and manage user-submitted feedback</p>
          </div>
          <Link
            to="/admin"
            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            ← Back to Admin
          </Link>
        </div>

        {/* Status Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { key: '', label: 'All', count: statusCounts.all, color: 'bg-gray-50 border-gray-200' },
            { key: 'new', label: 'New', count: statusCounts.new, color: 'bg-yellow-50 border-yellow-200' },
            { key: 'reviewed', label: 'Reviewed', count: statusCounts.reviewed, color: 'bg-blue-50 border-blue-200' },
            { key: 'resolved', label: 'Resolved', count: statusCounts.resolved, color: 'bg-green-50 border-green-200' },
          ].map(item => (
            <button
              key={item.key}
              onClick={() => setFilterStatus(item.key)}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                filterStatus === item.key ? 'ring-2 ring-blue-500' : ''
              } ${item.color}`}
            >
              <div className="text-2xl font-bold">{item.count}</div>
              <div className="text-sm font-medium text-gray-600">{item.label}</div>
            </button>
          ))}
        </div>

        {/* Type Filter */}
        <div className="flex items-center gap-2 mb-6">
          <span className="text-sm text-gray-600">Filter by type:</span>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option value="bug">Bug Reports</option>
            <option value="feature">Feature Requests</option>
            <option value="question">Questions</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Loading feedback...</p>
          </div>
        ) : feedback.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8 text-center">
            <p className="text-gray-500 text-lg">No feedback found</p>
            <p className="text-gray-400 text-sm mt-1">
              {filterStatus || filterType ? 'Try changing the filters' : 'Feedback will appear here when users submit it'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedback.map((item) => {
              const typeInfo = TYPE_LABELS[item.type] || TYPE_LABELS.other;
              const statusInfo = STATUS_OPTIONS.find(s => s.value === item.status);
              const isExpanded = expandedId === item.id;

              return (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : item.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="text-xl flex-shrink-0">{typeInfo.icon}</span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${typeInfo.color}`}>
                              {typeInfo.label}
                            </span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusInfo?.color || 'bg-gray-100'}`}>
                              {statusInfo?.label || item.status}
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(item.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-gray-900 text-sm line-clamp-2">{item.message}</p>
                          {item.email && (
                            <p className="text-xs text-gray-500 mt-1">From: {item.email}</p>
                          )}
                        </div>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Full Message</h4>
                        <p className="text-gray-800 whitespace-pre-wrap bg-white p-3 rounded-lg border border-gray-200 text-sm">
                          {item.message}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                        <div>
                          <span className="text-gray-500 font-medium">Email:</span>
                          <p className="text-gray-800">{item.email || 'Not provided'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 font-medium">User ID:</span>
                          <p className="text-gray-800 truncate">{item.userId || 'Anonymous'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 font-medium">Page:</span>
                          <p className="text-gray-800 truncate">{item.metadata?.url || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 font-medium">Submitted:</span>
                          <p className="text-gray-800">{new Date(item.createdAt).toLocaleString()}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Set status:</span>
                        {STATUS_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => handleStatusChange(item.id, opt.value)}
                            className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                              item.status === opt.value
                                ? `${opt.color} ring-2 ring-offset-1 ring-blue-500`
                                : `${opt.color} opacity-60 hover:opacity-100`
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
